import React from 'react';
import ReactDOM from 'react-dom/client';
import ShellSkeleton from './components/ShellSkeleton';
// app-shell.css must come BEFORE index.css and any MUI/Spectrum
// styles so its tokens win on .app-shell-scoped surfaces (DesignView).
import './styles/app-shell.css';
import './index.css';
import App from './App';
// Side-effect: applies the persisted light/dark theme to <html> pre-mount.
import './ajoTheme';

// The logout sentinel only matters WITHIN one page load (the race between
// tokenManager.clear() and the /signed-out navigation). If a logout was
// ever interrupted, the sentinel survived in the tab's sessionStorage and
// permanently blocked ProtectedRoute's sign-in redirect — the tab sat on
// the boot skeleton forever. A fresh boot on any page other than
// /signed-out can't have a logout genuinely in progress: clear it.
try {
  if (window.location.pathname !== '/signed-out') {
    sessionStorage.removeItem('ajo:logout-in-progress');
  }
} catch { /* sessionStorage may be blocked — best-effort */ }
// @ts-expect-error JS module
import ErrorBoundary from './components/ErrorBoundary';
// @ts-expect-error JS module
import { loadRuntimeConfig } from './services/config';

// Auto-recover from stale chunk references after a redeploy. Vite emits
// `vite:preloadError` when an `import('…some-hash.js')` 404s — that
// happens when a user's tab was loaded against a previous deploy and a
// lazy-loaded chunk's hash has rotated since (Mermaid's per-diagram
// chunks, route-level code splits, etc.). Force a hard reload exactly
// once so the next render picks up the fresh manifest. The flag in
// sessionStorage prevents an infinite loop if the chunk genuinely is
// missing for a non-deploy reason.
// Rate-limited (3 reloads / 2 min) instead of once-per-session: on a
// multi-deploy day a long-lived tab goes stale REPEATEDLY, and the old
// single-shot flag left every recurrence stuck on the boot skeleton
// forever. The cap still prevents a reload loop when a chunk is
// genuinely missing for a non-deploy reason.
// The reload must NOT fire mid-action: file uploads, staged-but-unsent
// composer attachments, and streaming chat turns all register in the
// window-scoped busy counter (src/services/busy.ts), and the recovery
// polls until the app is idle — with NO time cap. Agent turns run for
// many minutes; the old 90s cap reloaded the page mid-generation, which
// users saw as "the page reloads/blinks when I upload files". The busy
// counter is released in finally blocks, so idle always arrives; a
// genuinely broken chunk still recovers at the next idle moment.
let reloadPending = false;
function reloadWhenIdle(): void {
  if (reloadPending) return;
  reloadPending = true;
  const attempt = () => {
    const busy = ((window as any).__ajoBusyCount || 0) > 0;
    if (busy) {
      setTimeout(attempt, 1_000);
      return;
    }
    window.location.reload();
  };
  attempt();
}

window.addEventListener('vite:preloadError', () => {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem('ajo:preload-recoveries');
    const recent = (raw ? (JSON.parse(raw) as number[]) : [])
      .filter((t) => now - t < 120_000);
    if (recent.length >= 3) return;
    recent.push(now);
    sessionStorage.setItem('ajo:preload-recoveries', JSON.stringify(recent));
    reloadWhenIdle();
  } catch {
    /* sessionStorage may be blocked — best-effort */
  }
});

// Boot wrapper: waits for runtime config to load before rendering the
// app tree. Moves the blocking wait INSIDE React so we get first paint
// immediately (no white screen), while preserving the invariant that
// getConfig() is never called before config is resolved.
function Boot() {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    loadRuntimeConfig().finally(() => setReady(true));
  }, []);

  if (!ready) {
    // Structure beats a spinner: show the app's frame while config loads.
    return <ShellSkeleton />;
  }
  return <App />;
}

// Mount immediately. The Boot wrapper handles config loading internally.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Boot />
    </ErrorBoundary>
  </React.StrictMode>
);

// Lazy-load KaTeX styles after first paint (chat-only, not critical path).
// KaTeX library code is already lazy via react-markdown's rehype-katex
// (only loaded when DesignView mounts), so this defers the 200KB of font/CSS
// to the same boundary. Uses requestIdleCallback when available, otherwise
// a 0-delay setTimeout to yield the main thread for first paint.
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(() => {
    import('./styles/katex-lazy');
  });
} else {
  setTimeout(() => {
    import('./styles/katex-lazy');
  }, 0);
}
